import { useId, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { ROUTE_NAMES } from '@tsgecart/config';
import { cn } from '@tsgecart/ui';

export interface SearchBarProps {
  className?: string;
  /** Called after a successful submit -- lets the mobile menu close itself once the user searches. */
  onSubmitted?: () => void;
}

/**
 * The storefront's search entry point -- submitting navigates to
 * `ROUTE_NAMES.search?q=...`. The actual results page is built by another
 * agent; this component only needs to get the query onto that route.
 */
export function SearchBar({ className, onSubmitted }: SearchBarProps) {
  const [value, setValue] = useState('');
  const navigate = useNavigate();
  const inputId = useId();

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const query = value.trim();
    navigate(query ? `${ROUTE_NAMES.search}?q=${encodeURIComponent(query)}` : ROUTE_NAMES.search);
    onSubmitted?.();
  }

  return (
    <form role="search" onSubmit={handleSubmit} className={cn('relative w-full', className)}>
      <label htmlFor={inputId} className="sr-only">
        Search products
      </label>
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
        aria-hidden="true"
      />
      <input
        id={inputId}
        type="search"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder="Search for groceries, brands and more"
        className="focus-visible:ring-brand w-full rounded-full border border-gray-200 bg-gray-50 py-2.5 pl-9 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus-visible:border-transparent focus-visible:bg-white focus-visible:outline-none focus-visible:ring-2"
      />
    </form>
  );
}
